import type { Graph } from '../core/graph';
import { parseDocument } from '../core/serialize';
import type { FilterMode, GradientStop, ParamBag } from '../core/types';
import brickWallDocument from './preset-documents/brick-wall.json';
import { computeLayout } from './layout';

interface NodeSpec {
  id: string;
  type: string;
  params?: ParamBag;
  title?: string;
  x?: number;
  y?: number;
}

interface EdgeSpec {
  from: string;
  fromPort?: string;
  to: string;
  port: string;
}

export interface Preset {
  name: string;
  description: string;
  filtering: FilterMode;
  resolution?: number;
  nodes: NodeSpec[];
  edges: EdgeSpec[];
}

const publishedBrickWall = parseDocument(brickWallDocument);
/**
 * CC0-1.0 publication 52fec624-d465-416c-a070-351d0d1eec17, revision 2.
 * Body hash: d5d70c181b1c85b9f970a41744046723ecccd4a31e947b1f8415ebe826757d81.
 */
const brickWallPreset: Preset = {
  name: 'brick-wall',
  description: 'Aged running-bond brick with heavy mortar overflow, damp staining, and wear.',
  filtering: 'linear',
  resolution: 512,
  nodes: publishedBrickWall.nodes,
  edges: publishedBrickWall.edges.map(({ from, fromPort, to, toPort }) => ({
    from,
    fromPort,
    to,
    port: toPort,
  })),
};

const g = (stops: [number, [number, number, number]][]): GradientStop[] =>
  stops.map(([t, [r, gg, b]]) => ({ t, color: [r, gg, b, 1] }));

/**
 * Starter graphs.
 *
 * These are complete, working materials rather than fragments, for two reasons:
 * a new user gets something that looks right on first launch, and an agent has
 * a known-good structure to modify instead of assembling one from nothing —
 * which is both cheaper and far more likely to produce a usable result.
 */
export const PRESETS: Preset[] = [
  brickWallPreset,

  {
    name: 'rusted-metal',
    description: 'Pitted steel with rust blooming out of the low areas.',
    filtering: 'linear',
    nodes: [
      { id: 'base', type: 'gen.noise', params: { scale: 5, octaves: 8, gain: 0.55 } },
      {
        id: 'pits',
        type: 'gen.cellular',
        params: { mode: 'worley', scale: 22, falloff: 0.45, invert: true },
      },
      { id: 'pitMask', type: 'adjust.histogramScan', params: { position: 0.72, contrast: 0.8 } },
      {
        id: 'brushed',
        type: 'gen.noise',
        params: { type: 'value', scale: 48, octaves: 3, seed: 11 },
      },
      {
        id: 'streaks',
        type: 'filter.blurDirectional',
        params: { radius: 70, angle: 0.25, quality: 40 },
      },
      { id: 'surface', type: 'filter.blend', params: { mode: 'overlay', opacity: 0.45 } },
      { id: 'pitted', type: 'filter.blend', params: { mode: 'subtract', opacity: 0.5 } },
      { id: 'height', type: 'adjust.levels', params: { inBlack: 0.1, inWhite: 0.92 } },
      { id: 'normal', type: 'mesh.normal', params: { intensity: 6 } },
      { id: 'ao', type: 'mesh.ao', params: { radius: 12, intensity: 1.1 } },
      {
        id: 'rustNoise',
        type: 'gen.noise',
        params: { type: 'ridged', scale: 4, octaves: 7, seed: 21 },
      },
      {
        id: 'rustMask',
        type: 'adjust.histogramScan',
        params: { position: 0.48, contrast: 0.45 },
        title: 'Rust coverage',
      },
      {
        id: 'steel',
        type: 'adjust.gradientMap',
        params: {
          gradient: g([
            [0.0, [0.1, 0.11, 0.13]],
            [0.55, [0.34, 0.36, 0.39]],
            [1.0, [0.62, 0.65, 0.69]],
          ]),
        },
      },
      {
        id: 'rust',
        type: 'adjust.gradientMap',
        params: {
          gradient: g([
            [0.0, [0.14, 0.06, 0.03]],
            [0.4, [0.36, 0.14, 0.05]],
            [0.72, [0.58, 0.26, 0.09]],
            [1.0, [0.72, 0.44, 0.22]],
          ]),
        },
      },
      {
        id: 'albedo',
        type: 'filter.blend',
        params: { mode: 'copy', opacity: 1 },
        title: 'Rust over steel',
      },
      { id: 'metalMask', type: 'adjust.invert', params: {} },
      { id: 'metalLevels', type: 'adjust.levels', params: { inBlack: 0.35, inWhite: 0.75 } },
      {
        id: 'roughBase',
        type: 'adjust.levels',
        params: { inBlack: 0.2, inWhite: 0.85, outBlack: 0.18, outWhite: 0.42 },
      },
      { id: 'roughRust', type: 'filter.blend', params: { mode: 'lighten', opacity: 0.85 } },
      { id: 'roughRustVal', type: 'adjust.levels', params: { outBlack: 0.72, outWhite: 0.95 } },
      { id: 'outColor', type: 'output.channel', params: { channel: 'basecolor' } },
      { id: 'outNormal', type: 'output.channel', params: { channel: 'normal' } },
      { id: 'outRough', type: 'output.channel', params: { channel: 'roughness' } },
      { id: 'outMetal', type: 'output.channel', params: { channel: 'metallic' } },
      { id: 'outHeight', type: 'output.channel', params: { channel: 'height' } },
      { id: 'outAO', type: 'output.channel', params: { channel: 'ao' } },
    ],
    edges: [
      { from: 'brushed', to: 'streaks', port: 'input' },
      { from: 'base', to: 'surface', port: 'background' },
      { from: 'streaks', to: 'surface', port: 'foreground' },
      { from: 'pits', to: 'pitMask', port: 'input' },
      { from: 'surface', to: 'pitted', port: 'background' },
      { from: 'pitMask', to: 'pitted', port: 'foreground' },
      { from: 'pitted', to: 'height', port: 'input' },
      { from: 'height', to: 'normal', port: 'height' },
      { from: 'height', to: 'ao', port: 'height' },
      { from: 'rustNoise', to: 'rustMask', port: 'input' },
      { from: 'height', to: 'steel', port: 'input' },
      { from: 'height', to: 'rust', port: 'input' },
      { from: 'steel', to: 'albedo', port: 'background' },
      { from: 'rust', to: 'albedo', port: 'foreground' },
      { from: 'rustMask', to: 'albedo', port: 'mask' },
      { from: 'rustMask', to: 'metalMask', port: 'input' },
      { from: 'metalMask', to: 'metalLevels', port: 'input' },
      { from: 'height', to: 'roughBase', port: 'input' },
      { from: 'roughRustVal', to: 'roughRust', port: 'foreground' },
      { from: 'roughBase', to: 'roughRust', port: 'background' },
      { from: 'rustMask', to: 'roughRust', port: 'mask' },
      { from: 'rustMask', to: 'roughRustVal', port: 'input' },
      { from: 'albedo', to: 'outColor', port: 'input' },
      { from: 'normal', to: 'outNormal', port: 'input' },
      { from: 'roughRust', to: 'outRough', port: 'input' },
      { from: 'metalLevels', to: 'outMetal', port: 'input' },
      { from: 'height', to: 'outHeight', port: 'input' },
      { from: 'ao', to: 'outAO', port: 'input' },
    ],
  },

  {
    name: 'cracked-earth',
    description: 'Dried mud: cellular cracks warped into irregularity, with dust in the fissures.',
    filtering: 'linear',
    nodes: [
      // Voronoi draws the fissures as flat lines: white plates, black cracks,
      // no inversion — that would raise the cracks and sink the plates.
      { id: 'cells', type: 'gen.cellular', params: { mode: 'voronoi', scale: 9, veinWidth: 0.07 } },
      { id: 'warpNoise', type: 'gen.noise', params: { scale: 5, octaves: 5, seed: 5 } },
      { id: 'warped', type: 'filter.warp', params: { intensity: 0.05 } },
      // The flat mask has to become a height before it can read as mud, and
      // Distance is what turns any silhouette into one: Inside mode measures
      // depth within each plate, so the surface rises from the fissures rather
      // than standing on vertical walls. Roughly half a plate at this cell
      // count, which brings the ramp to full height around the centre.
      { id: 'depth', type: 'filter.distance', params: { mode: 'inside', maxDistance: 26 } },
      // A soft scan leaves the plates domed rather than flat-topped; a hard one
      // clips almost everything to white and the surface reads as bone.
      { id: 'plates', type: 'adjust.histogramScan', params: { position: 0.3, contrast: 0.28 } },
      { id: 'soften', type: 'filter.blur', params: { radius: 3 } },
      {
        id: 'grain',
        type: 'gen.noise',
        params: { scale: 30, octaves: 6, seed: 13, contrast: 0.8 },
      },
      {
        id: 'eroded',
        type: 'filter.slopeBlur',
        params: { intensity: 0.6, samples: 10, mode: 'min' },
      },
      { id: 'detail', type: 'filter.blend', params: { mode: 'overlay', opacity: 0.3 } },
      { id: 'height', type: 'adjust.levels', params: { inBlack: 0.05, inWhite: 0.95 } },
      { id: 'normal', type: 'mesh.normal', params: { intensity: 12 } },
      { id: 'ao', type: 'mesh.ao', params: { radius: 26, intensity: 1.2 } },
      {
        id: 'albedo',
        type: 'adjust.gradientMap',
        params: {
          gradient: g([
            [0.0, [0.07, 0.05, 0.03]],
            [0.35, [0.24, 0.16, 0.1]],
            [0.7, [0.36, 0.26, 0.17]],
            [1.0, [0.5, 0.4, 0.28]],
          ]),
        },
      },
      { id: 'aoTint', type: 'filter.blend', params: { mode: 'multiply', opacity: 0.9 } },
      { id: 'rough', type: 'adjust.levels', params: { outBlack: 0.72, outWhite: 0.96 } },
      { id: 'outColor', type: 'output.channel', params: { channel: 'basecolor' } },
      { id: 'outNormal', type: 'output.channel', params: { channel: 'normal' } },
      { id: 'outRough', type: 'output.channel', params: { channel: 'roughness' } },
      { id: 'outHeight', type: 'output.channel', params: { channel: 'height' } },
      { id: 'outAO', type: 'output.channel', params: { channel: 'ao' } },
    ],
    edges: [
      { from: 'cells', to: 'warped', port: 'input' },
      { from: 'warpNoise', to: 'warped', port: 'warp' },
      { from: 'warped', to: 'depth', port: 'mask' },
      { from: 'depth', to: 'plates', port: 'input' },
      { from: 'plates', to: 'soften', port: 'input' },
      { from: 'soften', to: 'eroded', port: 'input' },
      { from: 'grain', to: 'eroded', port: 'slope' },
      { from: 'eroded', to: 'detail', port: 'background' },
      { from: 'grain', to: 'detail', port: 'foreground' },
      { from: 'detail', to: 'height', port: 'input' },
      { from: 'height', to: 'normal', port: 'height' },
      { from: 'height', to: 'ao', port: 'height' },
      { from: 'height', to: 'albedo', port: 'input' },
      { from: 'albedo', to: 'aoTint', port: 'background' },
      { from: 'ao', to: 'aoTint', port: 'foreground' },
      { from: 'height', to: 'rough', port: 'input' },
      { from: 'aoTint', to: 'outColor', port: 'input' },
      { from: 'normal', to: 'outNormal', port: 'input' },
      { from: 'rough', to: 'outRough', port: 'input' },
      { from: 'height', to: 'outHeight', port: 'input' },
      { from: 'ao', to: 'outAO', port: 'input' },
    ],
  },

  {
    name: 'hex-scales',
    description: 'Scattered hexagonal plates — a compact tour of Tile Sampler and height blending.',
    filtering: 'linear',
    nodes: [
      {
        id: 'hex',
        type: 'gen.shape',
        params: { shape: 'ngon', sides: 6, size: 0.44, falloff: 0.5 },
      },
      // Tile rather than Tile Sampler: a hex grid needs every other row offset
      // by half a cell, and instances must not overlap. Tile Sampler's
      // overlapping max-composite turns the same shapes into an interlocking
      // mesh instead of discrete plates.
      {
        id: 'scatter',
        type: 'filter.tile',
        params: { columns: 6, rows: 7, rowOffset: 0.5, randomBrightness: 0.28, seed: 3 },
      },
      { id: 'backdrop', type: 'gen.noise', params: { scale: 14, octaves: 6, seed: 4 } },
      { id: 'backdropLow', type: 'adjust.levels', params: { outBlack: 0, outWhite: 0.28 } },
      { id: 'combined', type: 'filter.blend', params: { mode: 'lighten', opacity: 1 } },
      { id: 'height', type: 'adjust.levels', params: { inWhite: 0.95 } },
      { id: 'normal', type: 'mesh.normal', params: { intensity: 9 } },
      { id: 'ao', type: 'mesh.ao', params: { radius: 18, intensity: 1.2 } },
      {
        id: 'albedo',
        type: 'adjust.gradientMap',
        params: {
          gradient: g([
            [0.0, [0.04, 0.06, 0.08]],
            [0.4, [0.08, 0.2, 0.26]],
            [0.75, [0.15, 0.44, 0.48]],
            [1.0, [0.55, 0.82, 0.8]],
          ]),
        },
      },
      {
        id: 'rough',
        type: 'adjust.levels',
        params: { inBlack: 0.2, outBlack: 0.15, outWhite: 0.55 },
      },
      { id: 'outColor', type: 'output.channel', params: { channel: 'basecolor' } },
      { id: 'outNormal', type: 'output.channel', params: { channel: 'normal' } },
      { id: 'outRough', type: 'output.channel', params: { channel: 'roughness' } },
      { id: 'outHeight', type: 'output.channel', params: { channel: 'height' } },
      { id: 'outAO', type: 'output.channel', params: { channel: 'ao' } },
    ],
    edges: [
      { from: 'hex', to: 'scatter', port: 'input' },
      { from: 'backdrop', to: 'backdropLow', port: 'input' },
      { from: 'scatter', to: 'combined', port: 'background' },
      { from: 'backdropLow', to: 'combined', port: 'foreground' },
      { from: 'combined', to: 'height', port: 'input' },
      { from: 'height', to: 'normal', port: 'height' },
      { from: 'height', to: 'ao', port: 'height' },
      { from: 'height', to: 'albedo', port: 'input' },
      { from: 'height', to: 'rough', port: 'input' },
      { from: 'albedo', to: 'outColor', port: 'input' },
      { from: 'normal', to: 'outNormal', port: 'input' },
      { from: 'rough', to: 'outRough', port: 'input' },
      { from: 'height', to: 'outHeight', port: 'input' },
      { from: 'ao', to: 'outAO', port: 'input' },
    ],
  },
];

export function findPreset(name: string): Preset | undefined {
  return PRESETS.find((p) => p.name === name);
}

/**
 * Build a preset into `graph`, replacing its contents.
 * Preset ids are stable and human-readable, which makes them pleasant for an
 * agent to reference immediately after loading one.
 */
export function applyPreset(graph: Graph, preset: Preset): void {
  graph.clear();
  graph.setName(preset.name);
  graph.setFiltering(preset.filtering);
  if (preset.resolution) graph.setResolution(preset.resolution);
  for (const spec of preset.nodes) {
    graph.addNode(spec.type, { id: spec.id, params: spec.params, x: spec.x, y: spec.y });
    if (spec.title) graph.setNodeTitle(spec.id, spec.title);
  }
  for (const edge of preset.edges) {
    graph.connect(edge.from, edge.to, edge.port, edge.fromPort);
  }
  if (preset.nodes.some(({ x, y }) => x === undefined || y === undefined)) {
    for (const move of computeLayout(graph)) graph.moveNode(move.id, move.x, move.y);
  }
}
